import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, ShieldCheck, Compass } from 'lucide-react';
import { changePassword, logout } from '../services/auth';
import ConfirmButton from '../components/ConfirmButton';
import ProductTour from '../components/ProductTour';

const inputCls =
  'w-full bg-black border border-white/15 px-4 py-3 pr-16 text-sm text-white placeholder-gray-600 '
  + 'outline-none focus:border-white/40 tracking-wide disabled:opacity-50';

function PassInput({ label, value, onChange, placeholder, autoFocus, disabled }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-[9px] tracking-[0.25em] uppercase text-gray-500 mb-2 block">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete="off"
          className={inputCls}
        />
        <button type="button" onClick={() => setShow((s) => !s)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] tracking-[0.15em] uppercase text-gray-500 hover:text-white">
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tour, setTour] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (busy) return;

    // Checked here for a fast, clear message; the server enforces the same
    // rules again, since nothing from the browser can be trusted.
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== repeat) { setError('The two new passwords do not match.'); return; }
    if (next === current) { setError('New password must be different from the current one.'); return; }

    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent(''); setNext(''); setRepeat('');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white max-w-lg mx-auto px-6 pb-16 safe-top safe-bottom">
      <header className="pt-6 pb-6 border-b border-white/10">
        <button onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors mb-6">
          <ArrowLeft size={14} />
          <span className="text-[9px] tracking-[0.25em] uppercase">Back to Dashboard</span>
        </button>
        <p className="text-[9px] tracking-[0.35em] uppercase mb-2" style={{ color: 'var(--gold)' }}>
          Security
        </p>
        <h1 className="text-xl font-light tracking-[0.12em] uppercase">Settings</h1>
      </header>

      <main className="pt-8 space-y-8">
        {done ? (
          <div className="border-l-2 pl-4 py-2 space-y-3" style={{ borderColor: 'var(--gold)' }}>
            <p className="text-sm text-gray-200">Password changed.</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Every other device has been signed out. This one stays signed in.
              Use the new password from now on.
            </p>
            <button onClick={() => setDone(false)}
              className="text-[9px] tracking-[0.2em] uppercase text-gray-500 hover:text-white transition-colors">
              Change it again
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={13} style={{ color: 'var(--gold)' }} />
              <p className="text-[9px] tracking-[0.25em] uppercase text-gray-400">
                Change dashboard password
              </p>
            </div>
            <p className="text-[11px] text-gray-600 leading-relaxed">
              This is the password for signing in to the whole dashboard. Changing it
              signs out every other device, which is also how you remove access from a
              phone or laptop you no longer have.
            </p>

            <PassInput label="Current Password" value={current} disabled={busy}
              onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" autoFocus />
            <PassInput label="New Password" value={next} disabled={busy}
              onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" />
            <PassInput label="Repeat New Password" value={repeat} disabled={busy}
              onChange={(e) => setRepeat(e.target.value)} placeholder="Repeat new password" />

            {error && <p className="text-[11px] text-red-400/90 leading-relaxed">{error}</p>}

            <button type="submit" disabled={busy || !current || !next || !repeat}
              className="w-full text-[10px] tracking-[0.3em] uppercase font-semibold py-3.5 transition-opacity disabled:opacity-40"
              style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
              {busy ? 'Saving…' : 'Change Password'}
            </button>
          </form>
        )}

        <div className="pt-6 border-t border-white/8 space-y-4">
          <button
            onClick={() => setTour(true)}
            className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase
              text-gray-500 hover:text-white transition-colors"
          >
            <Compass size={13} /> Replay the product tour
          </button>

          <ConfirmButton
            onConfirm={logout}
            label="Sign out of this device"
            confirmLabel="Tap to confirm sign out"
            className="text-[9px] tracking-[0.2em] uppercase transition-colors hover:text-white"
            style={{ color: '#4b5563' }}
            confirmStyle={{ color: 'var(--gold)' }}
          />
          <div className="flex items-start gap-2">
            <ShieldCheck size={12} className="mt-0.5 shrink-0 text-gray-700" />
            <p className="text-[9px] text-gray-700 leading-relaxed">
              The password is never stored on the server, only a hash of it. Module 08
              keeps its own separate code, which is changed from inside that module.
            </p>
          </div>
        </div>
      </main>

      {tour && (
        <ProductTour onClose={() => { setTour(false); }} />
      )}
    </div>
  );
}
